import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { WebsocketService } from './websocket.service';
import {
  IAiWebsocketResponse,
  IMessage,
  IConversation,
  IUnreadMessagesState,
  IChatState
} from '../../interfaces/conversations.interfaces';
import { EMessageItemCategory, EMessagePayloadEvent } from '../../enums/conversation.enums';
import { ChatListService } from './chat-list.service';

// Constants for localStorage keys
const LAST_ACTIVE_CONVERSATION_ID_KEY = 'lastActiveConversationId';
const USER_ID_KEY = 'userId';
const ACCESS_TOKEN_KEY = 'accessToken';

@Injectable({
  providedIn: 'root'
})
export class ChatStateService {
  private activeConversationId: string | null = null;
  private unreadMessagesSubject = new BehaviorSubject<IUnreadMessagesState>({});
  private messageBuffer: { [conversationId: string]: IMessage[] } = {};
  private chatStates: { [conversationId: string]: IChatState } = {};
  private typingIndicatorsSubject = new BehaviorSubject<{ [conversationId: string]: boolean }>({});
  private pendingCreatorIdSubject = new BehaviorSubject<string | null>(null);
  private websocketSubscription: Subscription | null = null;
  private isInitialized: boolean = false;

  public unreadMessages$: Observable<IUnreadMessagesState> =
    this.unreadMessagesSubject.asObservable();
  public typingIndicators$ = this.typingIndicatorsSubject.asObservable();
  public pendingCreatorId$ = this.pendingCreatorIdSubject.asObservable();

  constructor(
    private websocketService: WebsocketService,
    private chatListService: ChatListService
  ) {
    this.restoreLastActiveConversation();
  }

  private restoreLastActiveConversation(): void {
    const lastActiveConversationId = localStorage.getItem(LAST_ACTIVE_CONVERSATION_ID_KEY);
    if (!lastActiveConversationId) return;

    this.activeConversationId = lastActiveConversationId;
    this.restoreTypingIndicatorIfNeeded(lastActiveConversationId);
  }

  private restoreTypingIndicatorIfNeeded(conversationId: string): void {
    const state = this.chatStates[conversationId];
    const creatorId = conversationId.split('-')[0];

    // Only restore typing indicator if it was active and we have a valid creator ID
    // The actual typing state will be managed by WebSocket events
    if (state?.isTyping && creatorId) {
      this.updateTypingIndicator(conversationId, true);
    }
  }

  private initializeWebSocketListener(): void {
    if (this.isInitialized) return;

    this.websocketSubscription?.unsubscribe();

    this.websocketSubscription = this.websocketService.onMessage().subscribe({
      next: (message: unknown) => {
        this.processWebSocketMessage(message as IAiWebsocketResponse);
      }
    });

    this.isInitialized = true;
  }

  private processWebSocketMessage(message: IAiWebsocketResponse): void {
    if (!message) return;

    // With unified WebSocket, messages now contain the full conversation_id
    const conversationId = message.conversation_id;
    if (!conversationId) return;

    if (this.isCommandEvent(message)) {
      this.handleCommandEvent(message.payload.event, conversationId);
      return;
    }

    if (this.isChatMessage(message)) {
      this.handleChatMessage(conversationId, message);
    }

    this.bufferMessageIfNotVisible(conversationId, message);
  }

  private isCommandEvent(message: IAiWebsocketResponse): boolean {
    return (
      message.category === EMessageItemCategory.EVENT &&
      message.payload.category === EMessageItemCategory.COMMAND
    );
  }

  private isChatMessage(message: IAiWebsocketResponse): boolean {
    return !!message.payload.message && message.category !== EMessageItemCategory.EVENT;
  }

  private handleChatMessage(conversationId: string, message: IAiWebsocketResponse): void {
    this.chatListService.updateLastMessage(conversationId, message.payload.message);

    if (!this.isConversationActiveAndVisible(conversationId)) {
      this.incrementUnreadCount(conversationId);
    }
  }

  private bufferMessageIfNotVisible(conversationId: string, message: IAiWebsocketResponse): void {
    if (this.isConversationActiveAndVisible(conversationId)) return;

    this.addMessageToBuffer(conversationId, message);
    this.stopTypingIndicatorIfBuffering(conversationId);
  }

  private isConversationActiveAndVisible(conversationId: string): boolean {
    return this.activeConversationId === conversationId && this.isActiveConversationVisible();
  }

  private stopTypingIndicatorIfBuffering(conversationId: string): void {
    if (this.chatStates[conversationId]?.isTyping) {
      this.updateTypingIndicator(conversationId, false);
    }
  }

  private handleCommandEvent(event: string, conversationId: string): void {
    if (event === EMessagePayloadEvent.TYPING_INDICATOR_ON) {
      this.updateTypingIndicator(conversationId, true);
    } else if (event === EMessagePayloadEvent.TYPING_INDICATOR_OFF) {
      this.updateTypingIndicator(conversationId, false);
    }
  }

  private updateTypingIndicator(conversationId: string, isTyping: boolean): void {
    this.ensureChatState(conversationId);

    const state = this.chatStates[conversationId];
    if (state.isTyping === isTyping) return;

    state.isTyping = isTyping;
    state.lastTypingTimestamp = isTyping ? Date.now() : state.lastTypingTimestamp || 0;

    const currentIndicators = this.typingIndicatorsSubject.value;
    this.typingIndicatorsSubject.next({
      ...currentIndicators,
      [conversationId]: isTyping
    });
  }

  private ensureChatState(conversationId: string): void {
    if (this.chatStates[conversationId]) return;

    this.chatStates[conversationId] = {
      isInitialized: false,
      lastConnected: Date.now(),
      isTyping: false,
      lastTypingTimestamp: 0
    };
  }

  private isActiveConversationVisible(): boolean {
    return window.location.pathname.includes('/chat') && document.visibilityState === 'visible';
  }

  private addMessageToBuffer(conversationId: string, message: IAiWebsocketResponse): void {
    const formattedMessage: IMessage = {
      from_stxt: true,
      category: message.category,
      payload: message.payload,
      timestamp: message.timestamp,
      conversation_id: conversationId
    };

    if (!this.messageBuffer[conversationId]) {
      this.messageBuffer[conversationId] = [];
    }

    const isDuplicate = this.messageBuffer[conversationId].some(
      existingMsg => existingMsg.timestamp === formattedMessage.timestamp
    );
    if (isDuplicate) return;

    this.messageBuffer[conversationId].push(formattedMessage);
  }

  public incrementUnreadCount(conversationId: string): void {
    const currentState = this.unreadMessagesSubject.value;
    const currentCount = currentState[conversationId] || 0;

    this.unreadMessagesSubject.next({
      ...currentState,
      [conversationId]: currentCount + 1
    });
  }

  public setActiveConversation(conversationId: string | null): void {
    if (this.activeConversationId === conversationId) return; // No change

    this.activeConversationId = conversationId;

    if (conversationId) {
      localStorage.setItem(LAST_ACTIVE_CONVERSATION_ID_KEY, conversationId);
      this.ensureChatState(conversationId);
    } else {
      localStorage.removeItem(LAST_ACTIVE_CONVERSATION_ID_KEY);
    }
  }

  public connectToChat(
    sessionToken: string,
    creatorId: string,
    performHandshake: boolean = false
  ): void {
    if (!sessionToken || !creatorId) return;
    const userId = localStorage.getItem(USER_ID_KEY);
    if (!userId) return;

    const conversationId = `${creatorId}-${userId}`;
    this.prepareConnection(conversationId);

    // Connect to WebSocket if not already connected
    if (!this.websocketService.isWebSocketConnected()) {
      this.websocketService.connect(sessionToken, true);
      this.initializeWebSocketListener();
    }

    // Perform handshake only when explicitly requested (for new conversations)
    if (performHandshake) {
      this.performHandshakeWhenReady(creatorId);
    }

    this.setActiveConversation(conversationId);
  }

  private prepareConnection(conversationId: string): void {
    this.ensureChatState(conversationId);
    this.chatStates[conversationId].isInitialized = true;
    this.chatStates[conversationId].lastConnected = Date.now();

    if (this.websocketService.isConnectionBlocked()) {
      this.websocketService.resetReconnectAttempts();
    }
  }

  private performHandshakeWhenReady(creatorId: string): void {
    // For new conversations, we should perform handshake immediately
    // even without connection ID to establish the connection and send hidden "hello"
    if (this.websocketService.isWebSocketConnected()) {
      this.websocketService.handshake(creatorId);
      return;
    }

    // If WebSocket is not connected yet, wait for it to connect and then perform handshake
    // This is needed for new conversations where we need to establish the connection
    const connectionCheck = setInterval(() => {
      if (this.websocketService.isWebSocketConnected()) {
        this.websocketService.handshake(creatorId);
        clearInterval(connectionCheck);
      }
    }, 100); // Check every 100ms

    // Set a timeout to prevent waiting indefinitely
    setTimeout(() => {
      clearInterval(connectionCheck);
    }, 5000); // 5 second timeout
  }

  public initializeAllConnections(conversations: IConversation[]): void {
    if (!conversations?.length) return;
    const sessionToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!sessionToken) return;

    this.resetConnectionStateForInitialization();

    // With unified connection, we just need to connect once
    this.websocketService.connect(sessionToken, true);
    this.initializeWebSocketListener();

    // Initialize chat states for all conversations
    const userId = localStorage.getItem(USER_ID_KEY);
    if (userId) {
      conversations.forEach(conversation => {
        const conversationId = conversation.conversation_id;
        this.ensureChatState(conversationId);
        this.chatStates[conversationId].isInitialized = true;
      });
    }
  }

  private resetConnectionStateForInitialization(): void {
    this.closeAllConnections();
  }

  private closeAllConnections(): void {
    this.websocketService.closeConnection();
  }

  public clearUnreadMessages(conversationId: string): void {
    const currentState = this.unreadMessagesSubject.value;
    if (!currentState[conversationId]) return;

    const newState = { ...currentState };
    delete newState[conversationId];
    this.unreadMessagesSubject.next(newState);
  }

  public getUnreadCount(conversationId: string): number {
    return this.unreadMessagesSubject.value[conversationId] || 0;
  }

  public getTotalUnreadCount(): number {
    return Object.values(this.unreadMessagesSubject.value).reduce(
      (total, count) => total + count,
      0
    );
  }

  public isTypingActive(conversationId: string): boolean {
    return this.chatStates[conversationId]?.isTyping ?? false;
  }

  public setPendingCreatorId(creatorId: string | null): void {
    this.pendingCreatorIdSubject.next(creatorId);
  }

  public cleanup(): void {
    this.websocketSubscription?.unsubscribe();
    this.websocketSubscription = null;

    this.messageBuffer = {};
    this.chatStates = {};
    this.activeConversationId = null;
    this.isInitialized = false;
    this.unreadMessagesSubject.next({});
    this.typingIndicatorsSubject.next({});
    this.pendingCreatorIdSubject.next(null);
  }
}
